import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import BorderedTextarea from 'src/components/form-new/bordered-textarea';
import BorderedSelect from 'src/components/form-new/bordered-select';
import BorderedToggle from 'src/components/form-new/bordered-toggle';
import useGenerateSurvey from 'src/hooks/useGenerateSurvey';
import { useGetTemplatesQuery } from 'src/redux/survey/template-endpoints';
import { SurveyTemplate } from 'src/redux/survey/template.types';
import ValidationStepsModal from 'src/components/moderation-dialog/moderation-dialog';
import {
  GENERATION_INTERVAL,
  GENERATION_LOADING_STEPS,
  GENERATION_LOADING_TITLE,
  RETRYING_GENERATION_TITLE,
} from 'src/pages/generate-survey/generate-survey.constants';
import { useMemo } from 'react';

interface AISurveyCreationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface StructuredData {
  required_inputs: { [key: string]: string };
  optional_inputs: { [key: string]: string };
  audience: string;
  is_rtr: boolean;
  rtr_size?: '300x250' | '970x250';
}

const AISurveyCreationModal: React.FC<AISurveyCreationModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [goal, setGoal] = useState<string>('');
  const [numberOfQuestions, setNumberOfQuestions] = useState<string>('10');
  const [isRTR, setIsRTR] = useState<boolean>(false);
  const [rtrSize, setRtrSize] = useState<'300x250' | '970x250'>('300x250');
  const [template, setTemplate] = useState<SurveyTemplate | null>(null);
  const { generateSurvey, isGenerating, isRetrying } = useGenerateSurvey();
  const { data: templatesData } = useGetTemplatesQuery(null);

  const { validationSteps, validationTitle } = useMemo(() => {
    const baseSteps = GENERATION_LOADING_STEPS;

    if (isRetrying) {
      baseSteps.unshift('Taking it from the top');
    }

    return {
      validationSteps: baseSteps,
      validationTitle: isRetrying ? RETRYING_GENERATION_TITLE : GENERATION_LOADING_TITLE,
    };
  }, [isGenerating, isRetrying]);

  // Find default template
  useEffect(() => {
    if (templatesData) {
      const allTemplates: SurveyTemplate[] = Object.values(templatesData).flat() as SurveyTemplate[];
      const defaultTemplate = allTemplates.find(t => t.category === 'DEFAULT');
      setTemplate(defaultTemplate || allTemplates[0] || null);
    }
  }, [templatesData]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setGoal('');
      setNumberOfQuestions('10');
      setIsRTR(false);
      setRtrSize('300x250');
    }
  }, [isOpen]);

  const handleSubmit = () => {
    if (!goal.trim() || !template) {
      return;
    }

    const structuredData: StructuredData = {
      required_inputs: {
        problem: goal.trim(),
      },
      optional_inputs: {
        number_of_questions: numberOfQuestions,
      },
      audience: '',
      is_rtr: isRTR,
      rtr_size: isRTR ? rtrSize : undefined,
    };

    void generateSurvey(template.id, structuredData);
  };

  const questionOptions = [
    { value: '1', label: '1 Question' },
    { value: '2', label: '2 Questions' },
    { value: '3', label: '3 Questions' },
    { value: '4', label: '4 Questions' },
    { value: '5', label: '5 Questions' },
    { value: '6', label: '6 Questions' },
    { value: '7', label: '7 Questions' },
    { value: '8', label: '8 Questions' },
    { value: '9', label: '9 Questions' },
    { value: '10', label: '10 Questions' },
  ];

  const rtrSizeOptions = [
    { value: '300x250', label: '300x250' },
    { value: '970x250', label: '970x250' },
  ];

  const isFormValid = goal.trim().length > 0 && template !== null;

  return (
    <>
      <Dialog
        open={isOpen}
        onOpenChange={onClose}
      >
        <DialogContent
          className="sm:max-w-[600px] bg-white"
          onClick={(e) => e.stopPropagation()}
        >
          <DialogHeader>
            <DialogTitle>
              Create Survey with AI
            </DialogTitle>
            <DialogDescription>
              Describe what you want to see in your survey.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Goal/Prompt Textarea */}
            <div className="space-y-2">
              <BorderedTextarea
                label="Survey Description"
                placeholder="Describe the purpose of your survey, what topics you want to cover, and any specific questions or themes you'd like to include..."
                value={goal}
                onChange={(e) => setGoal(e.target.value)}
                rows={6}
              />
            </div>

            {/* Number of Questions Selector */}
            <div className="space-y-2">
              <BorderedSelect
                label="Number of Questions"
                options={questionOptions}
                value={numberOfQuestions}
                onChange={(e) => setNumberOfQuestions(e.target.value)}
              />
            </div>

            {/* RTR Toggle */}
            <div className="space-y-2">
              <BorderedToggle
                label="RTR Survey"
                value={isRTR}
                onChange={setIsRTR}
                trueLabel="Yes"
                falseLabel="No"
              />
            </div>

            {/* RTR Size Selector (only shown when RTR is enabled) */}
            {isRTR && (
              <div className="space-y-2">
                <BorderedSelect
                  label="RTR Size"
                  options={rtrSizeOptions}
                  value={rtrSize}
                  onChange={(e) => setRtrSize(e.target.value as '300x250' | '970x250')}
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isGenerating}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!isFormValid || isGenerating}
            >
              {isGenerating ? 'Generating...' : 'Generate Survey'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ValidationStepsModal
        isModalOpen={isGenerating}
        handleCloseModal={() => {}}
        showFooter={false}
        steps={validationSteps}
        title={validationTitle}
        updateInterval={GENERATION_INTERVAL}
        windowSize={6}
      />
    </>
  );
};

export default AISurveyCreationModal;

